import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  TextInput,
  PasswordInput,
  NumberInput,
  Textarea,
  Switch,
  Tabs,
  Group,
  Stack,
  Paper,
  Alert,
  FileInput,
} from '@mantine/core';
import {
  Settings,
  AlertTriangle,
  CreditCard,
  Mail,
  Server,
  Shield,
  Tags,
  Globe,
} from 'lucide-react';

const tabIconStyle = { width: 18, height: 18 };

export default function Setup({ runtimeSecret }) {
  // Global
  const [adminUser, setAdminUser] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [systemId, setSystemId] = useState(crypto.randomUUID());
  const [baseUrl, setBaseUrl] = useState(window.location.origin);

  // License
  const [graceTime, setGraceTime] = useState(0);
  const [privateKeyText, setPrivateKeyText] = useState('');
  const [privateKeyFile, setPrivateKeyFile] = useState(null);
  const [licenseFileName, setLicenseFileName] = useState('');
  const [iss, setIss] = useState('');
  const fileInputRef = useRef(null);

  // Load current configuration when admin reconfigures (runtimeSecret present)
  useEffect(() => {
    if (!runtimeSecret) return;
    const h = { Authorization: `Bearer ${runtimeSecret}` };
    fetch('/api/stripe/setup', { headers: h })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load config');
        return res.json();
      })
      .then((data) => {
        if (data.adminUser) setAdminUser(data.adminUser);
        if (data.adminPassword) setAdminPassword(data.adminPassword);
        if (data.systemId) setSystemId(data.systemId);
        if (data.baseUrl) setBaseUrl(data.baseUrl);
        if (data.graceTime !== undefined) setGraceTime(data.graceTime);
        if (data.privateKey) setPrivateKeyText(data.privateKey);
        if (data.licenseFileName) setLicenseFileName(data.licenseFileName);
        if (data.iss) setIss(data.iss);
        if (data.emailSubject) setEmailSubject(data.emailSubject);
        if (data.emailBody) setEmailBody(data.emailBody);
        if (data.smtpHost) setSmtpHost(data.smtpHost);
        if (data.smtpPort !== undefined) setSmtpPort(data.smtpPort);
        if (data.smtpUser) setSmtpUser(data.smtpUser);
        if (data.smtpPassword) setSmtpPassword(data.smtpPassword);
        if (data.smtpSecure !== undefined) setSmtpSecure(data.smtpSecure);
        if (data.fromAddr) setFromAddr(data.fromAddr);
        if (data.secretKey) setStripeKey(data.secretKey);
        if (data.appsumoClientId) setAppsumoClientId(data.appsumoClientId);
        if (data.appsumoClientSecret) setAppsumoClientSecret(data.appsumoClientSecret);
        if (data.appsumoApiKey) setAppsumoApiKey(data.appsumoApiKey);
        if (data.appsumoHmacKey) setAppsumoHmacKey(data.appsumoHmacKey);
        if (data.proPrice) setProPrice(data.proPrice);
        if (data.enterprisePrice) setEnterprisePrice(data.enterprisePrice);
        if (data.proPriceId) setProPriceId(data.proPriceId);
        if (data.enterprisePriceId) setEnterprisePriceId(data.enterprisePriceId);
        if (data.authServerUrl) setAuthServerUrl(data.authServerUrl);
        if (data.authServerAppName) setAuthServerAppName(data.authServerAppName);
      })
      .catch((err) => setError(err.message));
  }, [runtimeSecret]);

  // Email
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // SMTP
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [fromAddr, setFromAddr] = useState('');

  // Stripe
  const [stripeKey, setStripeKey] = useState('');
  // Pricing
  const [proPrice, setProPrice] = useState('');
  const [enterprisePrice, setEnterprisePrice] = useState('');
  const [proPriceId, setProPriceId] = useState('');
  const [enterprisePriceId, setEnterprisePriceId] = useState('');

  // Appsumo
  const [appsumoClientId, setAppsumoClientId] = useState('');
  const [appsumoClientSecret, setAppsumoClientSecret] = useState('');
  const [appsumoApiKey, setAppsumoApiKey] = useState('');
  const [appsumoHmacKey, setAppsumoHmacKey] = useState('');

  // AuthServer storefront (hosted pricing + licenses + billing portal)
  const [authServerUrl, setAuthServerUrl] = useState('');
  const [authServerAppName, setAuthServerAppName] = useState('py2native');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = (file) => {
    setPrivateKeyFile(file);
    if (!file) {
      setPrivateKeyText('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPrivateKeyText(reader.result);
    reader.onerror = () => setError('Failed to read private key file.');
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adminUser || !adminPassword) {
      setError('Global: admin user and password are required.');
      return;
    }
    if (!systemId || !baseUrl) {
      setError('Global: System ID and Base URL are required.');
      return;
    }
    if (!privateKeyText) {
      setError('License: a private key file is required.');
      return;
    }
    if (!smtpHost || !smtpPort || !smtpUser || !smtpPassword || !fromAddr) {
      setError('SMTP: all fields are required.');
      return;
    }
    if (!stripeKey) {
      setError('Stripe: secret key is required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (runtimeSecret) {
        headers['Authorization'] = `Bearer ${runtimeSecret}`;
      }
      const response = await fetch('/api/stripe/setup', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          adminUser,
          adminPassword,
          systemId,
          baseUrl,
          graceTime,
          privateKey: privateKeyText,
          licenseFileName,
          iss,
          emailSubject,
          emailBody,
          smtpHost,
          smtpPort,
          smtpUser,
          smtpPassword,
          smtpSecure,
          fromAddr,
          secretKey: stripeKey,
          appsumoClientId,
          appsumoClientSecret,
          appsumoApiKey,
          appsumoHmacKey,
          proPrice,
          enterprisePrice,
          proPriceId,
          enterprisePriceId,
          authServerUrl,
          authServerAppName,
        }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Server returned ${response.status}`);
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container size="xs" py={80}>
        <Paper p="xl" radius="lg" withBorder>
          <Stack align="center" ta="center" gap="lg">
            <Settings size={48} color="var(--mantine-color-green-filled)" />
            <Title order={2}>Setup Complete</Title>
            <Text c="dimmed">
              The application has been configured. You may now reload the page to continue.
            </Text>
            <Button onClick={() => window.location.reload()}>
              Reload Application
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="md" py={80}>
      <Paper p="xl" radius="lg" withBorder>
        <Stack gap="lg">
          <Stack align="center" ta="center" gap="xs">
            <Settings size={48} color="var(--mantine-color-blue-filled)" />
            <Title order={2}>Application Setup</Title>
            <Text c="dimmed">
              Configure your Py2Native server before first use.
            </Text>
          </Stack>

          {error && (
            <Alert
              variant="light"
              color="red"
              icon={<AlertTriangle size={16} />}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="global">
              <Tabs.List grow>
                <Tabs.Tab value="global" leftSection={<Globe style={tabIconStyle} />}>
                  Global
                </Tabs.Tab>
                <Tabs.Tab value="license" leftSection={<Shield style={tabIconStyle} />}>
                  License
                </Tabs.Tab>
                <Tabs.Tab value="email" leftSection={<Mail style={tabIconStyle} />}>
                  Email
                </Tabs.Tab>
                <Tabs.Tab value="smtp" leftSection={<Server style={tabIconStyle} />}>
                  SMTP
                </Tabs.Tab>
                <Tabs.Tab value="stripe" leftSection={<CreditCard style={tabIconStyle} />}>
                  Stripe
                </Tabs.Tab>
                <Tabs.Tab value="authserver" leftSection={<Globe style={tabIconStyle} />}>
                  Storefront
                </Tabs.Tab>
                <Tabs.Tab value="appsumo" leftSection={<Tags style={tabIconStyle} />}>
                  Appsumo
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="global" pt="lg">
                <Stack gap="md">
                  <TextInput
                    label="Admin User"
                    description="Username for the admin account"
                    placeholder="admin"
                    value={adminUser}
                    onChange={(e) => setAdminUser(e.target.value)}
                    required
                  />
                  <PasswordInput
                    label="Admin Password"
                    description="Password for the admin account"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                  />
                  <TextInput
                    label="System ID"
                    description="Unique identifier for this installation"
                    value={systemId}
                    onChange={(e) => setSystemId(e.target.value)}
                    required
                  />
                  <TextInput
                    label="Web Page Base Address"
                    description="The public URL where this application is hosted"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    required
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="license" pt="lg">
                <Stack gap="md">
                  <NumberInput
                    label="Grace Time (days)"
                    description="Number of days a license remains valid after expiry"
                    value={graceTime}
                    onChange={(v) => setGraceTime(v)}
                    min={0}
                  />
                  <FileInput
                    ref={fileInputRef}
                    label="Private Key File"
                    description="PEM-encoded private key for license signing"
                    placeholder="Select .pem file"
                    value={privateKeyFile}
                    onChange={handleFileChange}
                    accept=".pem,.key,.txt"
                    required
                    clearable
                  />
                  <TextInput
                    label="License File Name"
                    description="Name of the license file distributed to users"
                    placeholder="license.dat"
                    value={licenseFileName}
                    onChange={(e) => setLicenseFileName(e.target.value)}
                  />
                  <TextInput
                    label="Issuer (iss)"
                    description="Issuer claim for JWT licenses"
                    placeholder="Py2Native"
                    value={iss}
                    onChange={(e) => setIss(e.target.value)}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="email" pt="lg">
                <Stack gap="md">
                  <TextInput
                    label="Email Subject"
                    description="Subject line for outgoing emails"
                    placeholder="Your Py2Native License"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                  <Textarea
                    label="Email Body"
                    description="Body template for outgoing emails"
                    placeholder="Thank you for your purchase..."
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    minRows={4}
                    autosize
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="smtp" pt="lg">
                <Stack gap="md">
                  <TextInput
                    label="SMTP Host"
                    placeholder="smtp.example.com"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    required
                  />
                  <NumberInput
                    label="SMTP Port"
                    description="Typically 587 (TLS) or 465 (SSL)"
                    value={smtpPort}
                    onChange={(v) => setSmtpPort(v)}
                    min={1}
                    max={65535}
                    required
                  />
                  <TextInput
                    label="SMTP User"
                    placeholder="user@example.com"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                    required
                  />
                  <PasswordInput
                    label="SMTP Password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    required
                  />
                  <Switch
                    label="SMTP Secure (TLS/SSL)"
                    description="Enable for TLS (port 587) or SSL (port 465)"
                    checked={smtpSecure}
                    onChange={(e) => setSmtpSecure(e.currentTarget.checked)}
                  />
                  <TextInput
                    label="From Address"
                    description="Sender email address for outgoing mail"
                    placeholder="noreply@example.com"
                    value={fromAddr}
                    onChange={(e) => setFromAddr(e.target.value)}
                    required
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="stripe" pt="lg">
                <Stack gap="md">
                  <PasswordInput
                    label="Stripe Secret Key"
                    description="Your Stripe secret key (starts with sk_live_ or sk_test_)"
                    placeholder="sk_live_..."
                    value={stripeKey}
                    onChange={(e) => setStripeKey(e.target.value)}
                    required
                  />
                  <TextInput
                    label="Pro Price ($)"
                    description="Display price for the Pro tier (e.g. 200)"
                    placeholder="200"
                    value={proPrice}
                    onChange={(e) => setProPrice(e.target.value)}
                  />
                  <TextInput
                    label="Enterprise Price ($)"
                    description="Display price for the Enterprise tier (e.g. 300)"
                    placeholder="300"
                    value={enterprisePrice}
                    onChange={(e) => setEnterprisePrice(e.target.value)}
                  />
                  <TextInput
                    label="Pro Stripe Price ID"
                    description="Stripe Price ID for the Pro tier (e.g. price_1xxx...)"
                    placeholder="price_1..."
                    value={proPriceId}
                    onChange={(e) => setProPriceId(e.target.value)}
                  />
                  <TextInput
                    label="Enterprise Stripe Price ID"
                    description="Stripe Price ID for the Enterprise tier (e.g. price_1xxx...)"
                    placeholder="price_1..."
                    value={enterprisePriceId}
                    onChange={(e) => setEnterprisePriceId(e.target.value)}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="appsumo" pt="lg">
                <Stack gap="md">
                  <TextInput
                    label="Client ID"
                    description="Appsumo OAuth client ID"
                    placeholder="client_..."
                    value={appsumoClientId}
                    onChange={(e) => setAppsumoClientId(e.target.value)}
                  />
                  <PasswordInput
                    label="Client Secret"
                    description="Appsumo OAuth client secret"
                    value={appsumoClientSecret}
                    onChange={(e) => setAppsumoClientSecret(e.target.value)}
                  />
                  <PasswordInput
                    label="API Key"
                    description="Appsumo API key"
                    value={appsumoApiKey}
                    onChange={(e) => setAppsumoApiKey(e.target.value)}
                  />
                  <PasswordInput
                    label="HMAC Key"
                    description="Appsumo HMAC signing key for webhook verification"
                    value={appsumoHmacKey}
                    onChange={(e) => setAppsumoHmacKey(e.target.value)}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="authserver" pt="lg">
                <Stack gap="md">
                  <Alert variant="light" color="blue" icon={<Globe size={16} />}>
                    The pricing page, Stripe checkout, license email/download
                    and the billing portal are hosted by the AuthServer. Leave
                    empty to keep the legacy Stripe checkout on this server.
                  </Alert>
                  <TextInput
                    label="AuthServer URL"
                    description="Base URL of the auth server, e.g. https://auth.example.com"
                    placeholder="https://auth.example.com"
                    value={authServerUrl}
                    onChange={(e) => setAuthServerUrl(e.target.value)}
                  />
                  <TextInput
                    label="App Name"
                    description="App name as registered in the auth server admin (used for pricing, license download & aud claim)"
                    placeholder="py2native"
                    value={authServerAppName}
                    onChange={(e) => setAuthServerAppName(e.target.value)}
                  />
                </Stack>
              </Tabs.Panel>
            </Tabs>

            <Group justify="flex-end" mt="xl">
              <Button
                type="submit"
                loading={loading}
                size="md"
              >
                Save Configuration
              </Button>
            </Group>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}